import { useEffect, useState } from 'react';
import { api, isConfigured } from './lib/api';
import { PROPERTIES } from '../../shared/types';
import AudienceSection from './sections/AudienceSection';
import MarketingSection from './sections/MarketingSection';
import SalesSection from './sections/SalesSection';
import EventsSection from './sections/EventsSection';
import SEOSection from './sections/SEOSection';
import FinanceSection from './sections/FinanceSection';

type SectionId = 'audience' | 'marketing' | 'sales' | 'events' | 'seo' | 'finance';

const SECTIONS: { id: SectionId; label: string }[] = [
  { id: 'audience', label: 'Audience & content' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'sales', label: 'Sales' },
  { id: 'events', label: 'Events' },
  { id: 'seo', label: 'SEO' },
  { id: 'finance', label: 'Finance' },
];

export default function App() {
  const [property, setProperty] = useState<string>('ALL');
  const [section, setSection] = useState<SectionId>('audience');
  const [sourceStatus, setSourceStatus] = useState<{
    sources: Array<{ name: string; enabled: boolean; reason?: string }>;
  } | null>(null);
  const [lastRun, setLastRun] = useState<any | null>(null);

  useEffect(() => {
    if (!isConfigured()) return;
    api.sources().then(setSourceStatus).catch(() => setSourceStatus(null));
    api
      .runLog()
      .then((d) => setLastRun(d.runs[0] ?? null))
      .catch(() => setLastRun(null));
  }, []);

  if (!isConfigured()) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="raised-card max-w-lg">
          <div className="text-sm font-medium mb-2">Frontend not yet wired to Worker</div>
          <div className="text-xs text-white/60 leading-relaxed">
            Set <code className="text-white">VITE_WORKER_URL</code> and{' '}
            <code className="text-white">VITE_DASHBOARD_API_KEY</code> as environment variables in
            Cloudflare Pages → Settings → Environment variables, then redeploy.
          </div>
        </div>
      </div>
    );
  }

  const enabledSources = sourceStatus?.sources.filter((s) => s.enabled) ?? [];
  const totalSources = sourceStatus?.sources.length ?? 0;

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <header className="mb-6">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-medium">Honeycombers Group — weekly ops</h1>
              <p className="text-xs text-white/50 mt-1">
                {lastRun ? (
                  <>
                    Last refresh: {new Date(lastRun.run_started_at as string).toLocaleString('en-AU')}{' '}
                    · <span className={lastRun.status === 'success' ? 'text-emerald-400' : 'text-amber-400'}>
                      {lastRun.status}
                    </span>{' '}
                    · {enabledSources.length}/{totalSources} sources live
                  </>
                ) : (
                  <>No refreshes yet — trigger one with `curl -X POST /run-now`</>
                )}
              </p>
            </div>
          </div>
        </header>

        {/* Property tabs */}
        <div className="flex gap-2 flex-wrap mb-5">
          {PROPERTIES.map((p) => (
            <button
              key={p.id}
              onClick={() => setProperty(p.id)}
              className={`pill-tab ${
                property === p.id ? 'pill-tab-active' : 'pill-tab-inactive'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Section nav */}
        <div className="border-b border-border-subtle mb-6">
          <div className="flex gap-1 overflow-x-auto -mb-px">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`px-3 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${
                  section === s.id
                    ? 'border-white text-white'
                    : 'border-transparent text-white/50 hover:text-white/80'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active section */}
        <main>
          {section === 'audience' && <AudienceSection property={property} />}
          {section === 'marketing' && <MarketingSection />}
          {section === 'sales' && <SalesSection />}
          {section === 'events' && <EventsSection />}
          {section === 'seo' && <SEOSection property={property} />}
          {section === 'finance' && <FinanceSection />}
        </main>

        {/* Source status footer */}
        {sourceStatus && (
          <footer className="mt-12 pt-6 border-t border-border-subtle text-xs text-white/40">
            <div className="font-medium mb-2">Data sources</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {sourceStatus.sources.map((s) => (
                <span
                  key={s.name}
                  title={s.reason ?? ''}
                  className={s.enabled ? 'text-emerald-400/80' : 'text-white/30'}
                >
                  {s.enabled ? '●' : '○'} {s.name}
                </span>
              ))}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
